import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    // Return safe defaults instead of throwing error
    return {
      user: null,
      login: () => Promise.resolve({ success: false, error: 'AuthContext not available' }),
      logout: () => {},
      isLoading: false,
      isAuthenticated: false
    };
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Auto-logout per inattività (1 ora = 3600000 ms)
  const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 ora in millisecondi

  // Initialize auth state on mount - Fixed version with useCallback to prevent race conditions
  const initializeAuth = useCallback(() => {
    console.log('🔄 AuthProvider: Initializing auth state...');
    
    try {
      const savedUser = localStorage.getItem('adfoodcost_user');
      if (savedUser) {
        const parsedUser = JSON.parse(savedUser);
        console.log('✅ AuthProvider: Found saved user:', parsedUser.email);
        
        // Verify the saved user has required fields
        if (parsedUser.id && parsedUser.email && parsedUser.name) {
          setUser(parsedUser);
          console.log('✅ AuthProvider: User restored from localStorage, ID:', parsedUser.id);
          
          // Double-check localStorage persistence after setting user
          setTimeout(() => {
            const verifyUser = localStorage.getItem('adfoodcost_user');
            if (!verifyUser) {
              console.error('❌ AuthProvider: localStorage verification failed - user lost after restore');
            } else {
              console.log('✅ AuthProvider: localStorage persistence verified');
            }
          }, 100);
        } else {
          console.log('⚠️ AuthProvider: Saved user missing required fields, clearing');
          localStorage.removeItem('adfoodcost_user');
          setUser(null);
        }
      } else {
        console.log('ℹ️ AuthProvider: No saved user found in localStorage');
        
        // Debug: Check if localStorage is working at all
        localStorage.setItem('test_storage', 'test_value');
        const testValue = localStorage.getItem('test_storage');
        if (testValue === 'test_value') {
          console.log('✅ AuthProvider: localStorage is working correctly');
          localStorage.removeItem('test_storage');
        } else {
          console.error('❌ AuthProvider: localStorage is not working!');
        }
        
        setUser(null);
      }
    } catch (error) {
      console.error('❌ AuthProvider: Error parsing saved user:', error);
      localStorage.removeItem('adfoodcost_user');
      setUser(null);
    }
    
    setIsLoading(false);
    console.log('✅ AuthProvider: Initialization complete');
  }, []); // Empty dependency array since this should not depend on external values

  // Variabile per il timer di auto-logout (must be defined before use)
  const inactivityTimerRef = useRef(null);
  
  // Funzione per avviare/resettare il timer di auto-logout (must be defined before use)
  const startInactivityTimer = useCallback(() => {
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    // Start new timer only if user is authenticated
    if (user) {
      inactivityTimerRef.current = setTimeout(() => {
        console.log('⏰ Auto-logout: Utente inattivo per 1 ora');
        // Logout con flag auto-logout
        setUser(null);
        localStorage.removeItem('adfoodcost_user');
        alert('Sessione scaduta per inattività. Effettuare nuovamente il login.');
      }, INACTIVITY_TIMEOUT);
    }
  }, [user, INACTIVITY_TIMEOUT]);

  useEffect(() => {
    console.log('🚀 AuthProvider: Mounting...');
    initializeAuth();
    
    // Listen for storage changes (for multiple tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'adfoodcost_user') {
        console.log('🔄 AuthProvider: Storage change detected, re-initializing');
        initializeAuth();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [initializeAuth]); // Include initializeAuth in dependency array

  // Effect per gestire il timer di inattività quando l'utente è autenticato
  useEffect(() => {
    if (user) {
      console.log('👤 Auto-logout: Avvio timer inattività per utente:', user.email);
      
      // Avvia il timer iniziale
      startInactivityTimer();
      
      // Eventi da tracciare per attività utente
      const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      
      // Aggiungi listener per tutti gli eventi di attività
      activityEvents.forEach(event => {
        document.addEventListener(event, startInactivityTimer, true);
      });
      
      // Cleanup quando l'utente non è più autenticato o component unmounts
      return () => {
        console.log('🧹 Auto-logout: Pulizia timer e listeners');
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
        activityEvents.forEach(event => {
          document.removeEventListener(event, startInactivityTimer, true);
        });
      };
    }
  }, [user, startInactivityTimer]);

  const login = async (credentials) => {
    console.log('🔐 Login attempt:', credentials.email);
    
    try {
      // Prima prova con i demo accounts hardcoded
      const demoAccounts = {
        'demo@adfoodcost.it': {
          password: 'demo',
          user: {
            id: '1',
            email: 'demo@adfoodcost.it',
            name: 'Demo Admin',
            role: 'admin',
            restaurant: 'Ristorante Demo'
          }
        },
        'chef@adfoodcost.it': {
          password: 'chef123',
          user: {
            id: '2',
            email: 'chef@adfoodcost.it',
            name: 'Chef Marco',
            role: 'chef',
            restaurant: 'Ristorante Bella Vista'
          }
        }
        // Account demo rimosso per sicurezza
      };

      // PRIORITA': Prova prima con il database utenti per gli utenti reali
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: credentials.email,
          username: credentials.email, // Prova sia email che username
          password: credentials.password
        })
      });

      if (response.ok) {
        const userData = await response.json();
        
        // Format user data consistently
        const formattedUserData = {
          ...userData,
          loginTime: new Date().toISOString(),
          restaurant: userData.restaurant || 'Ristorante'
        };

        console.log('✅ Login: Database user authentication successful');
        // Salva userData nel localStorage con tutti i campi
        console.log('💾 Saving user to localStorage:', formattedUserData);
        localStorage.setItem('adfoodcost_user', JSON.stringify(formattedUserData));
        setUser(formattedUserData);
        
        // Verify localStorage persistence after login (multiple checks)
        setTimeout(() => {
          const verifyLogin = localStorage.getItem('adfoodcost_user');
          if (verifyLogin) {
            console.log('✅ Login: localStorage persistence verified after database login');
            
            // Additional verification after longer delay
            setTimeout(() => {
              const verifyDelayedLogin = localStorage.getItem('adfoodcost_user');
              if (verifyDelayedLogin) {
                console.log('✅ Login: localStorage persistence verified after 1 second');
              } else {
                console.error('❌ Login: localStorage cleared after 1 second - possible cleanup interference');
              }
            }, 1000);
          } else {
            console.error('❌ Login: localStorage verification failed after database login');
          }
        }, 100);
        
        return { success: true };
      }

      // Se il login al database fallisce, prova con i demo account (fallback)
      const demoAccount = demoAccounts[credentials.email];
      
      if (demoAccount && demoAccount.password === credentials.password) {
        // Login con demo account come fallback
        const userData = {
          ...demoAccount.user,
          loginTime: new Date().toISOString()
        };

        console.log('⚠️ Login: Using demo account as fallback');
        localStorage.setItem('adfoodcost_user', JSON.stringify(userData));
        setUser(userData);
        
        // Verify localStorage persistence after demo login
        setTimeout(() => {
          const verifyDemo = localStorage.getItem('adfoodcost_user');
          if (verifyDemo) {
            console.log('✅ Login: localStorage persistence verified after demo login');
          } else {
            console.error('❌ Login: localStorage verification failed after demo login');
          }
        }, 100);
        
        return { success: true };
      }

      // Se nessuno dei due ha funzionato
      console.log('❌ Login: Account not found in demo or database');
      return { success: false, error: 'Account non trovato o password errata' };
      
    } catch (error) {
      console.error('❌ Login: Error during authentication:', error);
      return { success: false, error: 'Errore di connessione' };
    }
  };

  const logout = (isAutoLogout = false) => {
    console.log(`🚪 ${isAutoLogout ? 'Auto-logout' : 'Logout'}: Clearing user data`);
    
    // Clear timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    
    setUser(null);
    localStorage.removeItem('adfoodcost_user');
    console.log(`✅ ${isAutoLogout ? 'Auto-logout' : 'Logout'}: Complete`);
    
    // Mostra notifica per auto-logout
    if (isAutoLogout) {
      alert('Sessione scaduta per inattività. Effettuare nuovamente il login.');
    }
  };

  const isAuthenticated = !!user;

  const value = {
    user,
    login,
    logout,
    isLoading,
    isAuthenticated
  };

  console.log('📊 AuthProvider render:', { 
    hasUser: !!user, 
    isAuthenticated, 
    isLoading, 
    userEmail: user?.email 
  });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};